// Desktop session-token custody.
//
// A terminal's session token now lasts up to 14 days so a till survives a
// real outage (see DESKTOP_OFFLINE_SESSION_MIN in backend/routes/auth.py).
// A credential with that lifetime has no business sitting in localStorage
// where any page-context script could read it, so the Electron main process
// keeps it encrypted with the OS keychain (safeStorage) and the renderer
// holds it only in memory for the lifetime of the window.
//
// Plain browsers never call any of this — the web app authenticates with the
// httpOnly `session_token` cookie and has no token to store.

const LEGACY_KEY = 'pharmxai_token';

const hasBridge = () => typeof window !== 'undefined' && !!window.pharmxai?.getSessionToken;

let cached = null;
let hydrated = false;
let hydration = null;

// Must be awaited before the first authenticated request on desktop.
export const initDesktopToken = async () => {
  if (hydration) return hydration;
  if (hydrated) return cached;
  hydration = hydrateToken();
  try { return await hydration; } finally { hydration = null; }
};

const hydrateToken = async () => {
  if (!hasBridge()) {
    // Older desktop builds (no keychain bridge) and the plain web app.
    cached = typeof window !== 'undefined' ? localStorage.getItem(LEGACY_KEY) : null;
    hydrated = true;
    return cached;
  }
  cached = await window.pharmxai.getSessionToken();
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (!cached && legacy) {
    // One-time migration for terminals upgrading from a localStorage build.
    cached = legacy;
    await window.pharmxai.setSessionToken(legacy);
  }
  localStorage.removeItem(LEGACY_KEY);
  hydrated = true;
  return cached;
};

export const getDesktopToken = () => cached;

export const setDesktopToken = async (token) => {
  cached = token || null;
  hydrated = true;
  if (hasBridge()) {
    return window.pharmxai.setSessionToken(token || '');
  }
  if (token) localStorage.setItem(LEGACY_KEY, token);
  else localStorage.removeItem(LEGACY_KEY);
};

export const clearDesktopToken = () => setDesktopToken(null);
